import { useEffect, useMemo } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateErrorId, reportError } from "@/lib/errorReporting";

interface ErrorFallbackProps {
  error: Error;
  reset?: () => void;
}

/**
 * Common error boundary fallback used by route errorComponents.
 * Logs the error to error_logs (best-effort) and shows the user a friendly screen.
 */
export function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const router = useRouter();
  const errorId = useMemo(() => generateErrorId(), []);

  useEffect(() => {
    void reportError(error, {
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
      source: "ErrorBoundary",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    router.invalidate();
    reset?.();
  };

  const handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4 py-10">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          申し訳ありません、エラーが発生しました
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          一時的な問題が発生した可能性があります。もう一度お試しください。
        </p>
        <p className="mt-4 text-xs text-muted-foreground">
          エラーID: <code className="rounded bg-muted px-1.5 py-0.5 font-mono">{errorId}</code>
        </p>
        {import.meta.env.DEV && error?.message && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left font-mono text-xs text-destructive">
            {error.message}
          </pre>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={handleRetry}>再試行</Button>
          <Button variant="outline" onClick={handleReload}>
            リロード
          </Button>
          <Button variant="outline" asChild>
            <Link to="/dashboard">ダッシュボードへ</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/contact">サポートに連絡</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
